import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Loader2,
  Mail,
  Smartphone,
  ArrowLeft,
} from 'lucide-react';
import axios from 'axios';

const SuperAdminLogin = () => {
  const navigate = useNavigate();
  const { login, complete2FALogin } = useSuperAdmin();
  const [step, setStep] = useState('credentials'); // 'credentials' | '2fa'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [twoFaMethod, setTwoFaMethod] = useState('email');
  const [emailHint, setEmailHint] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(email, password);
      
      if (response.requires_2fa) {
        // 2FA required
        setTempToken(response.temp_token);
        setTwoFaMethod(response.method);
        setEmailHint(response.email_hint || '');
        setStep('2fa');
      } else {
        // No 2FA, direct login
        navigate('/super-admin/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFaSubmit = async (e) => {
    e.preventDefault();
    if (twoFaCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const api = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL, withCredentials: true });
      const res = await api.post('/api/super-admin/auth/verify-2fa', {
        temp_token: tempToken,
        code: twoFaCode,
        method: twoFaMethod
      });
      
      // Update context (sessionStorage + state) — no localStorage
      complete2FALogin(res.data.access_token, res.data.admin);
      
      navigate('/super-admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    try {
      const api = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL, withCredentials: true });
      await api.post(`/api/super-admin/auth/resend-2fa?temp_token=${tempToken}`);
      setError(''); // Clear any previous error
      alert('New code sent! Check your email (or console for mock)');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep('credentials');
    setTwoFaCode('');
    setTempToken('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
        <CardHeader className="text-center">
          <Link
            to="/login"
            className="mx-auto flex items-center justify-center w-16 h-16 rounded-xl overflow-hidden mb-4"
            data-testid="sa-login-logo-link"
            aria-label="Back to main login"
          >
            <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
          </Link>
          <CardTitle className="text-2xl text-white">EQUIPR CORE Admin Portal</CardTitle>
          <CardDescription className="text-slate-400">
            {step === 'credentials' ? 'Super Admin Access Only' : 'Two-Factor Authentication'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'credentials' ? (
            <form onSubmit={handleCredentialsSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email"
                  className="bg-slate-700/50 border-slate-600 text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-slate-700/50 border-slate-600 text-white"
                  required
                />
                <div className="flex justify-end">
                  <Link
                    to="/super-admin/forgot-password"
                    className="text-xs text-slate-400 hover:text-[#ffd108] transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#ffd108] hover:bg-[#e5bc07] text-black font-semibold"
                disabled={loading}
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleTwoFaSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="text-center mb-4">
                <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                  twoFaMethod === 'totp' ? 'bg-[#ffd108]/20' : 'bg-blue-500/20'
                }`}>
                  {twoFaMethod === 'totp' ? (
                    <Smartphone className="h-6 w-6 text-[#ffd108]" />
                  ) : (
                    <Mail className="h-6 w-6 text-blue-400" />
                  )}
                </div>
                <p className="text-slate-300">
                  {twoFaMethod === 'totp' 
                    ? 'Enter the code from your authenticator app'
                    : `Enter the code sent to ${emailHint}`}
                </p>
                {twoFaMethod === 'email' && (
                  <p className="text-xs text-slate-500 mt-1">(Check console/logs for mock email)</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="code" className="text-slate-300">Verification Code</Label>
                <Input
                  id="code"
                  type="text"
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="bg-slate-700/50 border-slate-600 text-white text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#ffd108] hover:bg-[#e5bc07] text-black font-semibold"
                disabled={loading || twoFaCode.length !== 6}
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>
                ) : (
                  'Verify'
                )}
              </Button>

              <div className="flex items-center justify-between pt-2">
                <Button type="button" variant="ghost" size="sm" onClick={goBack} className="text-slate-400">
                  <ArrowLeft className="mr-1 h-4 w-4" /> Back
                </Button>
                {twoFaMethod === 'email' && (
                  <Button type="button" variant="ghost" size="sm" onClick={handleResendCode} className="text-[#ffd108]" disabled={loading}>
                    Resend Code
                  </Button>
                )}
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminLogin;
